import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { IGutterAsMarginProps, IGutterProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { useTestId } from '../../hooks/useTestId/useTestId';
import {
  Placement,
  BOTTOM,
  BOTTOM_END,
  BOTTOM_START,
  IPlacementProps,
  LEFT,
  LEFT_END,
  LEFT_START,
  RIGHT,
  RIGHT_END,
  RIGHT_START,
  TOP,
  TOP_END,
  TOP_START
} from '../../fundamentals/placements/placements';
import { BASIC_COLOURS, BasicColour } from '../../fundamentals/colours/variants';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BasePath, BaseSvg } from '../../common/BaseElements';

export type TooltipArrowDirection = 'up' | 'right' | 'down' | 'left';

export interface ITooltipArrowProps
  extends IGutterAsMarginProps,
    // similar to direction just mapping for popper
    IPlacementProps {
  dark?: boolean;
  direction?: TooltipArrowDirection;
  large?: boolean;
  size?: 'small' | 'medium' | 'large';
  isTourModal?: boolean;
}

interface ITooltipArrowComponentProps extends IGutterProps {
  fill: string;
  rotate: number;
  width: string;
  height: string;
}

const background = (colour: string) => ({ background: colour });
export type TooltipArrowColour = BasicColour | 'darkTourModal';

export const tooltipArrowColours = ({
  base,
  eoraCoreColours
}: IColoursCollection): { tooltipArrowColours: Record<TooltipArrowColour, { background: string }> } => ({
  tooltipArrowColours: {
    [BASIC_COLOURS.BLACK]: background(base.black.colour),
    [BASIC_COLOURS.WHITE]: background(base.white.colour),
    darkTourModal: background(eoraCoreColours.BACKGROUND_COLOR.PANEL_COLOR_BACKGROUND)
  }
});

const makeSize = (small: string, big: string) => ({
  width: small,
  height: big
});

const sizeMap = {
  [SIZES.SMALL]: makeSize(PX_TO_REM[6], PX_TO_REM[12]),
  [SIZES.MEDIUM]: makeSize(PX_TO_REM[8], PX_TO_REM[13]),
  [SIZES.LARGE]: makeSize(PX_TO_REM[18], PX_TO_REM[36])
};

const rotationMap: Record<TooltipArrowDirection, number> = {
  up: 270,
  right: 0,
  down: 90,
  left: 180
};

const ArrowSvg = styled(BaseSvg)<ITooltipArrowComponentProps>`
  ${useGutterAsMarginCss()};
  ${({ rotate, width, height }) => css`
    transform: rotate(${rotate}deg);
    width: ${width};
    height: ${height};
  `}
`;

const ArrowPath = styled(BasePath)<ITooltipArrowComponentProps>`
  fill: ${({ fill }) => fill};
`;

const placementMap: Record<Placement, TooltipArrowDirection> = {
  [TOP_START]: 'down',
  [TOP]: 'down',
  [TOP_END]: 'down',
  [BOTTOM_START]: 'up',
  [BOTTOM]: 'up',
  [BOTTOM_END]: 'up',
  [RIGHT_START]: 'left',
  [RIGHT]: 'left',
  [RIGHT_END]: 'left',
  [LEFT_START]: 'right',
  [LEFT]: 'right',
  [LEFT_END]: 'right'
};

export const TooltipArrow: React.FC<ITooltipArrowProps> = ({ dark, large, size, direction = 'up', placement, isTourModal, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'TooltipArrow');
  const theme = useTheme();

  const computedDirection = placement && placementMap[placement];

  const arrowSize = () => {
    if (large) return sizeMap[SIZES.LARGE];
    if (size) return sizeMap[size];
    return sizeMap[SIZES.SMALL];
  };
  const { width, height } = arrowSize();
  const svgProps: ITooltipArrowComponentProps = {
    // eslint-disable-next-line unicorn/no-nested-ternary
    fill: theme.tooltipArrowColours[theme.isDarkMode && isTourModal ? 'darkTourModal' : dark ? BASIC_COLOURS.BLACK : BASIC_COLOURS.WHITE].background,
    width: width,
    height: height,
    rotate: rotationMap[(computedDirection ?? direction) as TooltipArrowDirection]
  };

  return (
    <ArrowSvg {...generateTestId()} {...rest} {...svgProps} fill='none' viewBox='0 0 18 36' xmlns='http://www.w3.org/2000/svg'>
      <ArrowPath {...svgProps} d='M0 36L17.0512 20.1318C18.3163 18.9544 18.3163 17.0456 17.0512 15.8682L0 0V36Z' />
    </ArrowSvg>
  );
};
